import React, { useId, useState } from "react";
import { MAX_TEXT_LENGTH } from "../../../utils/questionUtils";

export interface FloatingLabelFieldProps {
  /** Field label — shown as a placeholder that floats on focus / when filled. */
  label: string;
  /** Current value (controlled). */
  value: string;
  /** Value-change handler (receives the new string, not the event). */
  onChange: (value: string) => void;
  /** Test id forwarded to the control. */
  testId: string;
  /** Marks the control required (native `required` + `aria-required`). */
  required?: boolean;
  /** Disables the control and dims the floating label. */
  disabled?: boolean;
  /** Focus the control on mount. */
  autoFocus?: boolean;
  /** Render a multi-line `<textarea>` instead of a single-line `<input>`. */
  multiline?: boolean;
  /** Extra classes merged onto the wrapper (e.g. `flex-1`). */
  className?: string;
  /**
   * Id of an element that also names the control (e.g. the question headline).
   * When set it takes precedence over `aria-label` for the accessible name,
   * matching the Bool notes field.
   */
  ariaLabelledBy?: string;
  /**
   * Native input `type` (single-line only). Defaults to `"text"`. Callers use
   * `"email"` to get the iPad/mobile email keyboard for email fields; this is
   * a UX hint only — format validation itself is driven by `error`, since
   * `type="email"`'s native validity is easy for a form to silently bypass.
   */
  type?: "text" | "email";
  /**
   * Hints the on-screen keyboard shown on touch devices (e.g. `"numeric"` for
   * a vehicle year field), independent of `type`.
   */
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  /**
   * Format-validation message to show under the field (e.g. "Enter a valid
   * email address"). Callers compute this (typically only once the field is
   * non-empty) and are also responsible for gating Save on it — this prop only
   * renders the message and marks the control `aria-invalid`.
   */
  error?: string;
  /**
   * HTML autofill token (WCAG 2.1 SC 1.3.5 Identify Input Purpose). Set ONLY on
   * fields collecting the participant's own information — 1.3.5 covers the
   * user's own data, and a token on a field asking about someone else (an
   * employer's address, an emergency contact) would invite the browser to
   * autofill the wrong person's details. Callers resolve the token from
   * `(workflowName, fieldKey)`, never the field key alone: `streetAddress`,
   * `city`, `state` and `zipCode` exist on both `addressConfirmation` (the
   * participant's) and `employment` (the employer's). See `autocompleteTokens`.
   */
  autoComplete?: string;
}

/**
 * A text field whose label sits like a placeholder inside the empty control and
 * floats to the top border on focus / when it holds a value (IHA-388 Phase 2).
 * Shared across the workflow forms; mirrors the Bool notes field (IHA-382).
 *
 * The floating label is `aria-hidden`; the control takes its accessible name
 * from `aria-label`. When `required`, both the native `required` attribute and
 * `aria-required` are set.
 */
const FloatingLabelField: React.FC<FloatingLabelFieldProps> = ({
  label,
  value,
  onChange,
  testId,
  required,
  disabled,
  autoFocus,
  multiline,
  className,
  ariaLabelledBy,
  type = "text",
  inputMode,
  error,
  autoComplete,
}) => {
  const [focused, setFocused] = useState(false);
  const floated = focused || value.length > 0;
  const errorId = useId();
  const hasError = Boolean(error);

  const controlProps = {
    "data-testid": testId,
    value,
    maxLength: MAX_TEXT_LENGTH,
    required,
    "aria-required": required,
    "aria-label": label,
    "aria-labelledby": ariaLabelledBy,
    "aria-invalid": hasError,
    "aria-describedby": hasError ? errorId : undefined,
    autoComplete,
    disabled,
    autoFocus,
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(e.target.value),
    className: `w-full p-2 rounded-lg disabled:opacity-30 border ${
      hasError ? "border-red-600" : "border-primary"
    }`,
  };

  return (
    <div className={`relative${className ? ` ${className}` : ""}`}>
      {multiline ? (
        <textarea
          {...controlProps}
          className={`${controlProps.className} min-h-[8rem]`}
        />
      ) : (
        <input
          type={type}
          inputMode={inputMode}
          {...controlProps}
          className={`${controlProps.className} min-h-[44px]`}
        />
      )}
      {/* Placeholder that floats to the top border on focus / when filled.
          aria-hidden: the control already has its accessible name via aria-label. */}
      <span
        aria-hidden
        className={`pointer-events-none absolute left-2 transition-all duration-200 ${
          floated
            ? "-top-2 bg-white px-1 text-xs text-primary"
            : "top-2 text-base text-[#515964]"
        }${disabled ? " opacity-30" : ""}`}
      >
        {label}
      </span>
      {hasError && (
        <p
          id={errorId}
          data-testid={`${testId}-error`}
          className="mt-1 text-xs text-red-600"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default FloatingLabelField;
